import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  FileText, 
  CheckCircle,
  AlertCircle,
  Download
} from 'lucide-react';
import api from '../../lib/api';
import { ContractViewer } from '../../components/contracts/ContractViewer';
import { SignatureCapture } from '../../components/contracts/SignatureCapture';
import { SignatureStatus } from '../../components/contracts/SignatureStatus';
import { QRCodeVerification } from '../../components/contracts/QRCodeVerification';

interface ContractSignature {
  id: string;
  signerRole: string;
  signerName: string;
  signerEmail: string;
  signerDocument: string;
  status: 'PENDING' | 'SIGNED' | 'DECLINED' | 'EXPIRED';
  signedAt?: string;
  order: number;
}

interface DigitalContract {
  id: string;
  content: string;
  variables: Record<string, string>;
  status: string;
  verificationCode: string;
  template?: {
    name: string;
    category: string;
  };
  signatures: ContractSignature[];
}

interface SignatureEvidence {
  biometricImage: string;
  biometricHash: string;
  geolocation?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  deviceFingerprint: string;
  ipAddress: string;
  userAgent: string;
  timestamp: number;
}

export function ContractSign() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<'review' | 'sign' | 'complete'>('review');
  const [error, setError] = useState<string | null>(null);

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract-sign', id],
    queryFn: async () => {
      const response = await api.get<DigitalContract>(`/contracts/${id}`);
      return response.data;
    },
  });

  // Find my pending signature
  const mySignature = contract?.signatures.find(s => s.status === 'PENDING');

  const signMutation = useMutation({
    mutationFn: async (evidence: SignatureEvidence) => {
      await api.post(`/contracts/${id}/sign`, {
        biometricData: evidence.biometricImage,
        biometricHash: evidence.biometricHash,
        geolocation: evidence.geolocation,
        deviceFingerprint: evidence.deviceFingerprint,
        timestamp: evidence.timestamp,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-sign', id] });
      setStep('complete');
    },
    onError: (err) => {
      setError('Erro ao assinar contrato. Tente novamente.');
      console.error(err);
    },
  });

  const handleSign = (evidence: SignatureEvidence) => {
    signMutation.mutate(evidence);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/contracts/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato-${contract?.verificationCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Contrato não encontrado</p>
      </div>
    );
  }

  if (!mySignature && step !== 'complete') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Contrato já assinado</h2>
          <p className="text-gray-600 mb-6">
            Você já assinou este contrato ou não há assinatura pendente para você.
          </p>
          <button
            onClick={() => navigate('/banking/contracts')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Voltar aos Contratos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/banking/contracts')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  {step === 'complete' ? 'Assinatura Concluída' : 'Assinar Contrato'}
                </h1>
                <p className="text-sm text-gray-500">
                  {contract.template?.name} • #{contract.verificationCode.slice(0, 8)}
                </p>
              </div>
            </div>
            {step !== 'sign' && (
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Step: Review */}
        {step === 'review' && (
          <div className="space-y-6">
            {/* Contract content */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-500" />
                  Conteúdo do Contrato
                </h2>
              </div>
              <ContractViewer
                content={contract.content}
                variables={contract.variables}
                status={contract.status as 'DRAFT' | 'PENDING_SIGNATURES' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED'}
                className="h-96"
              />
            </div>

            {/* Signatures status */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Status das Assinaturas</h3>
              <SignatureStatus signatures={contract.signatures} showTimeline />
            </div>

            {/* Action */}
            <div className="flex justify-end gap-4">
              <button
                onClick={() => navigate('/banking/contracts')}
                className="px-6 py-3 border rounded-lg hover:bg-gray-50"
              >
                Voltar
              </button>
              <button
                onClick={() => setStep('sign')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Li e concordo - Prosseguir para assinatura
              </button>
            </div>
          </div>
        )}

        {/* Step: Sign */}
        {step === 'sign' && mySignature && (
          <SignatureCapture
            contractId={contract.id}
            signerName={mySignature.signerName}
            signerDocument={mySignature.signerDocument}
            onSign={handleSign}
            onCancel={() => setStep('review')}
          />
        )}

        {/* Step: Complete */}
        {step === 'complete' && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-green-800 mb-4">
              Contrato Assinado com Sucesso!
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Sua assinatura foi registrada com sucesso. Um certificado de assinatura foi gerado e você receberá uma cópia por email.
            </p>

            {/* QR Code */}
            <div className="mb-8">
              <p className="text-sm text-gray-500 mb-4">
                Use o QR Code abaixo para verificar a autenticidade do documento:
              </p>
              <QRCodeVerification
                verificationCode={contract.verificationCode}
                size="md"
              />
            </div>

            <div className="flex justify-center gap-4">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-6 py-3 border rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                Baixar PDF
              </button>
              <button
                onClick={() => navigate('/banking/contracts')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Voltar aos Contratos
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContractSign;
